/**
 * Sheet-2 row 13 - GET /projects is paginated for platform_admin (page cap 200, ordered
 * createdAt desc, id asc). Walks the remaining pages after page 1 and reports, rather than hides,
 * every way the walk can end up with a partial list:
 *   - truncated: more pages exist than MAX_PROJECT_PAGES;
 *   - failedPage: a later page failed (partial list);
 *   - shortfall: after de-duplicating by id the unique count is below the server's last totalCount
 *     (offset paging on a newest-first order shifts rows when projects are created mid-walk,
 *     which duplicates some rows and can skip others).
 * Rows are de-duplicated by id (first occurrence wins).
 */
export const MAX_PROJECT_PAGES = 50;

export async function collectProjectPages(firstData, fetchPage) {
    const seen = new Set();
    const projects = [];
    const add = (rows) => {
        for (const p of rows || []) {
            if (!p || seen.has(p.id)) continue;
            seen.add(p.id);
            projects.push(p);
        }
    };
    add(firstData.projects);
    const pg = firstData.pagination;
    let lastTotalCount = pg ? pg.totalCount : projects.length;
    let truncated = false;
    let failedPage = null;
    if (pg) {
        const last = Math.min(pg.totalPages, MAX_PROJECT_PAGES);
        for (let next = (pg.page || 1) + 1; next <= last; next++) {
            let data;
            try {
                data = await fetchPage(next);
            } catch (e) {
                failedPage = next;
                break;
            }
            if (!data) {
                failedPage = next;
                break;
            }
            add(data.projects);
            if (data.pagination && Number.isFinite(data.pagination.totalCount)) lastTotalCount = data.pagination.totalCount;
        }
        truncated = pg.totalPages > MAX_PROJECT_PAGES;
    }
    const shortfall = !truncated && failedPage === null && projects.length < lastTotalCount;
    const warnings = [];
    if (truncated) warnings.push(`Only the first ${MAX_PROJECT_PAGES * (pg.pageSize || 200)} projects were loaded (${pg.totalCount} exist). Use search or filters to find the rest.`);
    if (failedPage !== null) warnings.push(`Project list is incomplete: page ${failedPage} failed to load. Refresh to retry.`);
    if (shortfall) warnings.push('Project list may be incomplete because projects changed while loading. Refresh to reload.');
    return { projects, truncated, failedPage, shortfall, warnings };
}
